import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
  Card,
  Box,
  Typography,
  Stack,
  Avatar,
  Divider,
  Grid,
} from "@mui/material";
import {
  RocketLaunchOutlined as RocketIcon,
  ScheduleOutlined as ClockIcon,
  ArrowForward as ArrowForwardIcon,
  Verified as VerifiedIcon,
  LocalFireDepartment as FireIcon,
  Numbers as NumbersIcon,
} from "@mui/icons-material";
import moment from "moment";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { DEFAULT_CURRENCY, LOTTERY_NAMES, LOTTERY_TYPES } from "../../../../../static/Constants";
import { safeFromWei } from "../../../../../static/utils";
import getImageUrlByHash from "../../../../../static/logoUtils";
import { CommonButton, CommonChip } from "../../../../Components/ui";

// Tier preview images
import HypersonicImg from "../../../../../static/Images/Hypersonic.png";
import BallasticImg from "../../../../../static/Images/Ballastic.png";
import CruisImg from "../../../../../static/Images/cruis.png";
import InitiationImg from "../../../../../static/Images/Initiation.png";

const TIER_CONFIG = {
  1: {
    name: "Initiation",
    img: InitiationImg,
    level: "Level 1",
    color: colors.emerald[600],
    bgColor: colors.emerald[50],
  },
  2: {
    name: "Cruise",
    img: CruisImg,
    level: "Level 2",
    color: colors.blue[600],
    bgColor: colors.blue[50],
  },
  3: {
    name: "Ballistic",
    img: BallasticImg,
    level: "Level 3",
    color: colors.red[600],
    bgColor: colors.red[50],
  },
  4: {
    name: "Hypersonic",
    img: HypersonicImg,
    level: "Level 4",
    color: colors.amber[600],
    bgColor: colors.amber[50],
  },
};

const AllMissileCard = ({
  lottery,
  partner,
  partnerLogo,
  currency = DEFAULT_CURRENCY,
}) => {
  const history = useHistory();

  const lotteryId = lottery?.lotteryId;
  const statusId = lottery?.status;
  const isRollover =
    statusId === "3" ||
    statusId === "5" ||
    Boolean(lottery?.rolloverperct && Number(lottery.rolloverperct) > 0);

  const totalPrizeFormatted = useMemo(
    () => safeFromWei(lottery?.totalPrize),
    [lottery?.totalPrize]
  );

  const entryFeeFormatted = useMemo(
    () => safeFromWei(lottery?.entryFee),
    [lottery?.entryFee]
  );

  const capacity = Number(lottery?.capacity) || 20;

  // Determine tier info based on level or capacity fallback
  const tierLevel = useMemo(() => {
    if (lottery?.level) {
      return Math.min(4, Math.max(1, Number(lottery.level)));
    }
    // Estimate tier by capacity or default to 4 (Hypersonic)
    if (capacity <= 20) return 4;
    if (capacity <= 50) return 3;
    if (capacity <= 100) return 2;
    return 1;
  }, [lottery?.level, capacity]);

  const tier = TIER_CONFIG[tierLevel] || TIER_CONFIG[4];

  const deployedDateFormatted = useMemo(() => {
    if (!lottery?.deployedOn) return null;
    return moment.unix(Number(lottery.deployedOn)).format("DD MMM YYYY");
  }, [lottery?.deployedOn]);

  const partnerLogoUrl = useMemo(() => {
    if (!partner?.logoHash) return null;
    return getImageUrlByHash(partner.logoHash, partnerLogo);
  }, [partner?.logoHash, partnerLogo]);

  const partnerDisplayName = useMemo(() => {
    if (partner?.name && partner.name.trim()) return partner.name;
    if (partner?.companyName && partner.companyName.trim()) return partner.companyName;
    const addr = lottery?.partnerAddress || lottery?.partner || "";
    if (addr && addr.length > 10) {
      return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
    }
    return "Verified Creator";
  }, [partner, lottery]);

  const handlePlayNow = () => {
    history.push(`/missilelottery/${lotteryId}`);
  };

  return (
    <Card
      className="missile-lottery-card"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: colors.dashboard.cardBg,
        borderRadius: borderRadiusPx.xl,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        position: "relative",
        overflow: "hidden",
        transition: "all 0.25s ease",
        "&:hover": {
          borderColor: colors.red[400],
          boxShadow: "0 12px 28px rgba(239, 68, 68, 0.12)",
          transform: "translateY(-4px)",
        },
      }}
    >
      {/* Top Accent Gradient Bar */}
      <Box
        sx={{
          height: 4,
          background: "linear-gradient(135deg, #f97316 0%, #ef4444 100%)",
        }}
      />

      <Box sx={{ p: 2.75, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* Top Badges Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          sx={{ mb: 2 }}
        >
          {/* Game Type Badge */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: borderRadiusPx.md,
                bgcolor: colors.red[50],
                color: colors.red[600],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RocketIcon sx={{ fontSize: 18 }} />
            </Box>
            <div>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: colors.red[700],
                  lineHeight: 1.1,
                  fontSize: "0.85rem",
                }}
              >
                {`Autobet ${LOTTERY_NAMES.ROCKETBET} #${lotteryId}`}
              </Typography>
              {deployedDateFormatted && (
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.slate[400],
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: "0.72rem",
                    mt: 0.25,
                  }}
                >
                  <ClockIcon sx={{ fontSize: 11 }} />
                  {deployedDateFormatted}
                </Typography>
              )}
            </div>
          </Stack>

          {/* Status Chip */}
          {isRollover ? (
            <CommonChip
              icon={<FireIcon sx={{ fontSize: 14 }} />}
              label="Rollover"
              variant="warning"
              size="small"
            />
          ) : (
            <CommonChip
              label="Live Draw"
              variant="success"
              size="small"
            />
          )}
        </Stack>

        {/* Missile Visual Stage / Progressive Tier Preview Banner */}
        <Box
          sx={{
            p: 1.5,
            borderRadius: borderRadiusPx.lg,
            bgcolor: colors.slate[900],
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2.5,
            position: "relative",
            overflow: "hidden",
            boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.4)",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              component="img"
              src={tier.img}
              alt={tier.name}
              sx={{
                width: 38,
                height: 38,
                objectFit: "contain",
                filter: "drop-shadow(0 2px 6px rgba(239, 68, 68, 0.4))",
              }}
            />
            <div>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  color: colors.neutral.white,
                  fontSize: "0.82rem",
                  letterSpacing: 0.3,
                }}
              >
                {tier.name} Missile
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: tier.color,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: "0.7rem",
                }}
              >
                {tier.level}
              </Typography>
            </div>
          </Stack>

          <Box
            sx={{
              px: 1.25,
              py: 0.35,
              borderRadius: borderRadiusPx.full,
              bgcolor: "rgba(255, 255, 255, 0.1)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[300],
                fontWeight: 600,
                fontSize: "0.7rem",
                display: "flex",
                alignItems: "center",
                gap: 0.4,
              }}
            >
              <NumbersIcon sx={{ fontSize: 11 }} />
              1 of {capacity}
            </Typography>
          </Box>
        </Box>

        {/* Prize Pool Main Display */}
        <Box
          sx={{
            p: 2,
            borderRadius: borderRadiusPx.lg,
            bgcolor: colors.slate[50],
            border: `1px solid ${colors.slate[200]}`,
            mb: 2.5,
            textAlign: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: colors.slate[500],
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.8,
              fontSize: "0.7rem",
              display: "block",
              mb: 0.5,
            }}
          >
            Chance to Win
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              color: colors.slate[900],
              letterSpacing: -0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              fontSize: { xs: "1.5rem", sm: "1.75rem" },
            }}
          >
            {totalPrizeFormatted}{" "}
            <Typography
              component="span"
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: colors.red[600],
                fontSize: "0.95rem",
              }}
            >
              {currency}
            </Typography>
          </Typography>
        </Box>

        {/* Stats Grid: Entry Fee & Capacity */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid item xs={6}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: borderRadiusPx.md,
                bgcolor: colors.slate[50],
                border: `1px solid ${colors.slate[100]}`,
                height: "100%",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: colors.slate[400], fontWeight: 600, display: "block", fontSize: "0.7rem" }}
              >
                Ticket Price
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: colors.slate[800], mt: 0.25 }}
              >
                {entryFeeFormatted} {currency}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: borderRadiusPx.md,
                bgcolor: colors.slate[50],
                border: `1px solid ${colors.slate[100]}`,
                height: "100%",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: colors.slate[400], fontWeight: 600, display: "block", fontSize: "0.7rem" }}
              >
                Total Slots
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: colors.slate[800], mt: 0.25 }}
              >
                {capacity} Trajectories
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ flexGrow: 1 }} />

        <Divider sx={{ mb: 2, borderColor: colors.slate[100] }} />

        {/* Partner Creator Info */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            {partnerLogoUrl ? (
              <Avatar
                src={partnerLogoUrl}
                alt={partnerDisplayName}
                sx={{ width: 26, height: 26, borderRadius: borderRadiusPx.sm }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 26,
                  height: 26,
                  bgcolor: colors.red[100],
                  color: colors.red[700],
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  borderRadius: borderRadiusPx.sm,
                }}
              >
                {partnerDisplayName.charAt(0).toUpperCase()}
              </Avatar>
            )}
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: colors.slate[600],
                fontWeight: 600,
                fontSize: "0.78rem",
                maxWidth: 150,
              }}
            >
              {partnerDisplayName}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center">
            <VerifiedIcon sx={{ fontSize: 14, color: colors.blue[500] }} />
            <Typography
              variant="caption"
              sx={{ color: colors.blue[600], fontWeight: 600, fontSize: "0.7rem" }}
            >
              Verified
            </Typography>
          </Stack>
        </Stack>

        {/* Action Button */}
        <CommonButton
          variant="primary"
          fullWidth
          onClick={handlePlayNow}
          endIcon={<ArrowForwardIcon />}
          sx={{
            py: 1.2,
            fontWeight: 700,
            background: LOTTERY_TYPES[3].gradient,
            boxShadow: LOTTERY_TYPES[3].boxShadow,
            "&:hover": {
              background: LOTTERY_TYPES[3].gradient,
              boxShadow: `0 6px 20px ${LOTTERY_TYPES[3].glow}`,
            },
          }}
        >
          Play Now
        </CommonButton>
      </Box>
    </Card>
  );
};

AllMissileCard.propTypes = {
  lottery: PropTypes.object.isRequired,
  partner: PropTypes.object,
  partnerLogo: PropTypes.array,
  currency: PropTypes.string,
};

export default AllMissileCard;
